import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Register.css';
import { SUPABASE_READY, supabase } from '../lib/supabaseClient';

function fileExt(file) {
  const parts = file.name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : 'jpg';
}

function Register() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userCatches, setUserCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageMsg, setPageMsg] = useState('');

  const [signup, setSignup] = useState({
    name: '',
    email: '',
    password: '',
    username: '',
    photo: null,
    feeConfirmed: false,
    weeklyUpdates: true,
  });
  const [signupMsg, setSignupMsg] = useState('');

  const [signin, setSignin] = useState({ email: '', password: '' });
  const [signinMsg, setSigninMsg] = useState('');

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');

  const [selectedCatch, setSelectedCatch] = useState(null);
  const [catchPhotoUrl, setCatchPhotoUrl] = useState('');
  const [catchPhotoLoading, setCatchPhotoLoading] = useState(false);
  const [catchPhotoError, setCatchPhotoError] = useState(false);
  const modalCloseRef = useRef(null);

  const activeUser = session?.user || null;

  const speciesProgress = useMemo(() => {
    const uniqueSpecies = new Set(userCatches.map((entry) => entry.species)).size;
    return { uniqueSpecies };
  }, [userCatches]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    []
  );

  const formatSubmissionDate = (value) => {
    if (!value) return 'Unknown date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';
    return dateFormatter.format(date);
  };

  useEffect(() => {
    if (!SUPABASE_READY) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function init() {
      const sessionResponse = await supabase.auth.getSession();
      const error = sessionResponse?.error;
      if (mounted) {
        if (error) {
          setPageMsg(error.message || 'Could not load session.');
        }
        setSession(sessionResponse?.data?.session || null);
        setLoading(false);
      }
    }

    init();

    const authStateResponse = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    const subscription = authStateResponse?.data?.subscription;

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!SUPABASE_READY || !activeUser) {
      setProfile(null);
      setUserCatches([]);
      return;
    }

    async function loadProfileAndCatches() {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', activeUser.id)
        .maybeSingle();
      setProfile(profileData || null);

      const { data: catchesData } = await supabase
        .from('catches')
        .select('id, species, category, length_inches, description, photo_path, submitted_at')
        .eq('user_id', activeUser.id)
        .order('submitted_at', { ascending: false });
      setUserCatches(catchesData || []);
    }

    loadProfileAndCatches();
  }, [activeUser]);

  async function uploadProfilePhoto(userId, file) {
    const ext = fileExt(file);
    const path = `${userId}/profile-${Date.now()}.${ext}`;

    const { error } = await supabase.storage
      .from('profile-photos')
      .upload(path, file, { upsert: true });

    if (error) throw error;
    return path;
  }

  const onSignupChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === 'photo') {
      setSignup((prev) => ({ ...prev, photo: files?.[0] || null }));
      return;
    }
    setSignup((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const onCreateProfile = async (e) => {
    e.preventDefault();
    setSignupMsg('');
    setPageMsg('');

    if (!SUPABASE_READY) {
      setSignupMsg('Supabase is not configured yet. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in Netlify.');
      return;
    }

    if (!signup.photo) {
      setSignupMsg('Please add a profile photo.');
      return;
    }

    if (!signup.feeConfirmed) {
      setSignupMsg('Please confirm your $20 derby fee before creating your account.');
      return;
    }

    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: signup.email.trim(),
        password: signup.password,
      });

      if (signUpError) {
        setSignupMsg(signUpError.message);
        return;
      }

      const signedInUser = signUpData.user;
      if (!signedInUser) {
        setSignupMsg('Check your email to confirm your account, then sign in.');
        return;
      }

      const photoPath = await uploadProfilePhoto(signedInUser.id, signup.photo);

      let { error: profileError } = await supabase.from('profiles').upsert({
        id: signedInUser.id,
        email: signup.email.trim(),
        username: signup.username.trim(),
        display_name: signup.name.trim(),
        profile_photo_path: photoPath,
        fee_confirmed: signup.feeConfirmed,
        weekly_updates: signup.weeklyUpdates,
      });

      if (profileError && String(profileError.message || '').includes('fee_confirmed')) {
        const retry = await supabase.from('profiles').upsert({
          id: signedInUser.id,
          email: signup.email.trim(),
          username: signup.username.trim(),
          display_name: signup.name.trim(),
          profile_photo_path: photoPath,
          weekly_updates: signup.weeklyUpdates,
        });
        profileError = retry.error;
        if (!profileError) {
          setPageMsg('Account created. Optional DB update: add a fee_confirmed column to profiles to report payment status.');
        }
      }

      if (profileError) {
        setSignupMsg(profileError.message);
        return;
      }

      setSignup({
        name: '',
        email: '',
        password: '',
        username: '',
        photo: null,
        feeConfirmed: false,
        weeklyUpdates: true,
      });

      setSignupMsg('Profile created. If email confirmation is enabled, confirm your email then sign in.');
    } catch (err) {
      setSignupMsg(err.message || 'Could not create profile.');
    }
  };

  const onSignInChange = (e) => {
    const { name, value } = e.target;
    setSignin((prev) => ({ ...prev, [name]: value }));
  };

  const onSignIn = async (e) => {
    e.preventDefault();
    setSigninMsg('');
    setPageMsg('');

    if (!SUPABASE_READY) {
      setSigninMsg('Supabase is not configured yet.');
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: signin.email.trim(),
      password: signin.password,
    });

    if (error) {
      setSigninMsg(error.message);
      return;
    }

    setSignin({ email: '', password: '' });
    setSigninMsg('Signed in.');
  };

  const onSignOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setUserCatches([]);
  };

  const openCatchModal = async (entry) => {
    setSelectedCatch(entry);
    setCatchPhotoUrl('');
    setCatchPhotoError(false);

    if (!entry.photo_path) {
      setCatchPhotoLoading(false);
      return;
    }

    setCatchPhotoLoading(true);
    const { data, error } = await supabase.storage
      .from('catch-photos')
      .createSignedUrl(entry.photo_path, 3600);

    if (error || !data?.signedUrl) {
      setCatchPhotoError(true);
      setCatchPhotoLoading(false);
      return;
    }

    setCatchPhotoUrl(data.signedUrl);
    setCatchPhotoLoading(false);
  };

  const closeCatchModal = () => {
    setSelectedCatch(null);
    setCatchPhotoUrl('');
    setCatchPhotoError(false);
  };

  useEffect(() => {
    if (!selectedCatch) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedCatch(null);
        setCatchPhotoUrl('');
        setCatchPhotoError(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedCatch]);

  useEffect(() => {
    if (!selectedCatch) return;
    modalCloseRef.current?.focus();
  }, [selectedCatch]);

  const onSendReset = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    if (!SUPABASE_READY) {
      setForgotMsg('Supabase is not configured yet.');
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: 'https://unofficialfishingderby.com/reset-password',
    });
    if (error) {
      setForgotMsg(error.message);
      return;
    }
    setForgotMsg('✅ Check your email — a reset link has been sent.');
    setForgotEmail('');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="card">
          <p>Loading account details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="page-container">
      <h1 className="page-title">Create Account & Sign In</h1>
      <p className="page-subtitle">
        Create your account with email and password. Once signed in, go to Submit Catch to upload derby entries.
      </p>

      {!SUPABASE_READY && (
        <div className="card error-card">
          <p>Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to run account features.</p>
        </div>
      )}

      {pageMsg && (
        <div className="card error-card">
          <p>{pageMsg}</p>
        </div>
      )}

      {!profile && <div className="card">
        <h2>Create Profile</h2>
        <form onSubmit={onCreateProfile} className="register-form" encType="multipart/form-data">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input id="name" name="name" type="text" value={signup.name} onChange={onSignupChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input id="email" name="email" type="email" value={signup.email} onChange={onSignupChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="signupPassword">Password *</label>
              <input
                id="signupPassword"
                name="password"
                type="password"
                value={signup.password}
                onChange={onSignupChange}
                minLength="8"
                required
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="feeConfirmed"
                  checked={signup.feeConfirmed}
                  onChange={onSignupChange}
                />
                <span>
                  I have paid or will pay the $20 derby fee. * Cash payments can be made to Finn.
                  E-transfer is available and can be sent to finnras12@icloud.com.
                </span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="weeklyUpdates"
                  checked={signup.weeklyUpdates}
                  onChange={onSignupChange}
                />
                <span>Send me weekly leaderboard updates by email</span>
              </label>
            </div>
            <div className="form-group"></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                id="username"
                name="username"
                type="text"
                value={signup.username}
                onChange={onSignupChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="profilePhoto">Profile Photo *</label>
              <input
                id="profilePhoto"
                name="photo"
                type="file"
                accept="image/*"
                onChange={onSignupChange}
                required
              />
            </div>
          </div>

          {signupMsg && <p className="form-note">{signupMsg}</p>}
          <button type="submit" className="btn btn-primary">Create Profile</button>
        </form>
        </div>}

        {!profile && <div className="card">
          <h2>Sign In</h2>
        <form onSubmit={onSignIn} className="signin-form">
          <div className="form-row single-row">
            <div className="form-group">
              <label htmlFor="signinEmail">Email</label>
              <input
                id="signinEmail"
                name="email"
                type="email"
                value={signin.email}
                onChange={onSignInChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signinPassword">Password</label>
              <input
                id="signinPassword"
                name="password"
                type="password"
                value={signin.password}
                onChange={onSignInChange}
                required
              />
            </div>
          </div>
          <div className="signin-actions">
            <button type="submit" className="btn btn-secondary">Sign In</button>
          </div>
          <div className="forgot-link-row">
            <button
              type="button"
              className="btn-link"
              onClick={() => { setShowForgot((v) => !v); setForgotMsg(''); }}
            >
              {showForgot ? '← Back to sign in' : 'Forgot password?'}
            </button>
          </div>
          {signinMsg && <p className="form-note">{signinMsg}</p>}
        </form>

        {showForgot && (
          <form onSubmit={onSendReset} className="forgot-form">
            <p className="forgot-instructions">Enter your account email and we'll send a reset link.</p>
            <div className="form-group">
              <label htmlFor="forgotEmail">Email</label>
              <input
                id="forgotEmail"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            {forgotMsg && <p className="form-note">{forgotMsg}</p>}
            <button type="submit" className="btn btn-outline">Send Reset Link</button>
          </form>
        )}

      </div>}

      {!profile && (
        <div className="card profile-footnote">
          <p>Sign in first, then head to the submission page to post your fish entries.</p>
        </div>
      )}
    </div>

    {profile && (
      <>
        <div className="card profile-hero">
          <ProfilePhoto profile={profile} size={96} />
          <div className="profile-hero-info">
            <h2 className="profile-hero-name">{profile.display_name}</h2>
            <p className="profile-hero-username">@{profile.username}</p>
            <p className="profile-hero-stats">
              {speciesProgress.uniqueSpecies} unique {speciesProgress.uniqueSpecies === 1 ? 'species' : 'species'} &middot; {userCatches.length} {userCatches.length === 1 ? 'submission' : 'submissions'}
            </p>
            <div className="profile-hero-actions">
              <Link to="/submit" className="btn btn-primary">+ Submit a Catch</Link>
              <button type="button" className="btn btn-outline" onClick={onSignOut}>Sign Out</button>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="submissions-heading">My Submissions</h2>
          {userCatches.length === 0 ? (
            <p className="submissions-empty">No catches yet. <Link to="/submit">Submit your first catch!</Link></p>
          ) : (
            <div className="submissions-grid">
              {userCatches.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="submission-tile"
                  onClick={() => openCatchModal(entry)}
                  aria-label={`View ${entry.species} submission`}
                >
                  <CatchThumb entry={entry} />
                  <div className="submission-tile-info">
                    <span className="submission-tile-species">{entry.species}</span>
                    <span className="submission-tile-meta">
                      <span className="submission-category-badge">{entry.category || 'General'}</span>
                      <span className="submission-tile-date">{formatSubmissionDate(entry.submitted_at)}</span>
                    </span>
                    <span className="submission-tile-length">{entry.length_inches ? `${Number(entry.length_inches).toFixed(1)} in` : ''}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </>
    )}

    {selectedCatch && (
      <div className="entry-modal-backdrop" role="dialog" aria-modal="true" onClick={closeCatchModal}>
        <div className="entry-modal profile-catch-modal" onClick={(e) => e.stopPropagation()}>
          <div className="entry-modal-header">
            <h3 id="profile-catch-modal-title">{selectedCatch.species} &mdash; {selectedCatch.category}</h3>
            <button ref={modalCloseRef} type="button" className="entry-modal-close" onClick={closeCatchModal} aria-label="Close">×</button>
          </div>
          <div className="entry-modal-content">
            <div className="entry-photo-wrap">
              {catchPhotoLoading ? (
                <p className="entry-photo-loading">Loading photo...</p>
              ) : catchPhotoUrl ? (
                <img src={catchPhotoUrl} alt={selectedCatch.species} className="entry-photo" />
              ) : catchPhotoError ? (
                <p className="entry-photo-loading">Photo could not be loaded.</p>
              ) : (
                <p className="entry-photo-loading">No photo available.</p>
              )}
            </div>
            <div className="entry-details">
              <p><strong>Species:</strong> {selectedCatch.species}</p>
              <p><strong>Category:</strong> {selectedCatch.category}</p>
              {selectedCatch.length_inches ? <p><strong>Length:</strong> {Number(selectedCatch.length_inches).toFixed(1)} in</p> : null}
              <p><strong>Submitted:</strong> {formatSubmissionDate(selectedCatch.submitted_at)}</p>
              {selectedCatch.description && <p><strong>Description:</strong> {selectedCatch.description}</p>}
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

function CatchThumb({ entry }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(Boolean(entry?.photo_path));
  const [error, setError] = useState(false);

  useEffect(() => {
    let canceled = false;

    async function loadThumb() {
      setError(false);
      setUrl('');

      if (!entry?.photo_path || !supabase) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase.storage
        .from('catch-photos')
        .createSignedUrl(entry.photo_path, 3600);

      if (canceled) return;
      if (error || !data?.signedUrl) {
        setError(true);
        setLoading(false);
        return;
      }

      setUrl(data.signedUrl);
      setLoading(false);
    }

    loadThumb();
    return () => {
      canceled = true;
    };
  }, [entry]);

  if (loading) {
    return <div className="catch-thumb catch-thumb-loading">Loading photo...</div>;
  }

  if (!url || error) {
    return <div className="catch-thumb catch-thumb-fallback">🐟</div>;
  }

  return <img src={url} alt={entry.species} className="catch-thumb" onError={() => setError(true)} onLoad={() => setLoading(false)} />;
}

function ProfilePhoto({ profile }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(Boolean(profile?.profile_photo_path));
  const [error, setError] = useState(false);

  useEffect(() => {
    let canceled = false;

    async function loadPhoto() {
      setError(false);
      setUrl('');

      if (!profile?.profile_photo_path || !supabase) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(profile.profile_photo_path, 3600);

      if (canceled) return;
      if (error || !data?.signedUrl) {
        setError(true);
        setLoading(false);
        return;
      }

      setUrl(data.signedUrl);
      setLoading(false);
    }

    loadPhoto();
    return () => {
      canceled = true;
    };
  }, [profile]);

  if (loading) {
    return <div className="profile-avatar profile-avatar-loading">Loading...</div>;
  }

  if (!url || error) {
    return <div className="profile-avatar profile-avatar-fallback">👤</div>;
  }

  return <img src={url} alt={profile.display_name} className="profile-avatar" onError={() => setError(true)} onLoad={() => setLoading(false)} />;
}

export default Register;
